import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AccountFeatureService, FeatureAccessType } from "@services/accountFeature.service";
import { NavigationService } from "@services/navigation.service";
import { VeriDate } from "@services/veriDate.service";
import { RouteStatus } from "@tms/_enums/RouteStatus";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { showStateTrigger, visibleStateTrigger } from "@tms/_routes/activeRoutes/active-routes.animations";
import { ActiveRoutesService, IRouteDetailDisplay, IRouteDetailVisitDisplay } from "@tms/_services/active-routes.service";
import { PlannedRouteService } from "@tms/_services/planned-route.service";
import * as moment from "moment";
import "./active-routes.component.scss";
import { TranslationService } from "@app/services/translation.service";

@Component({
    selector: 'active-routes',
    template: require('./active-routes.component.html'),
    animations: [visibleStateTrigger, showStateTrigger]
})
export class ActiveRoutesComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("viewport") viewport: CdkVirtualScrollViewport;
    @ViewChild("nowIndicator") nowIndicator: ElementRef<HTMLDivElement>;

    @ViewChild('calendarTitle') calendarTitle: ElementRef<HTMLDivElement>;
    @ViewChild('calendar') calendarContainer: ElementRef<HTMLDivElement>;

    // ---

    calendarContainerWidth: number;

    filterTabs: IFilterBarTab<IRouteDetailDisplay>[] = [];

    activeRoutes: IRouteDetailDisplay[] = [];
    filteredRoutes: IRouteDetailDisplay[] = [];
    displayRoutes: IRouteDetailDisplay[] = [];

    showAsTimeline: boolean;

    selectedRoute: IRouteDetailDisplay;

    updateInterval: ReturnType<typeof setInterval>;

    hoursToDisplay: {
        label: string,
        time: moment.Moment,
    }[] = [];

    screenWidth = 1200;
    leftNavBuffer = 250;
    pixelsPerHour = 200;
    currentTimePosition = 0;
    loading = false;

    panLeftClickPressed = false;
    panLeftClickInitialX = 0;
    panLeftDistanceTravelledX = 0;
    panLeftOriginalOffsetX = 0;
    panLeftOffsetX = 0;

    swimlaneHeight = 70;
    scrollOffsetY = 0;
    scrollContainerHeight = 0;

    calendarPanPosition = 0;
    showSyncButtonRight = false;
    showSyncButtonLeft = false;

    middleScreenTime: moment.Moment;

    rowHeight: number = 70;

    canManageTMS: boolean;

    constructor(
        private readonly accountFeatureService: AccountFeatureService,
        private readonly activeRoutesService: ActiveRoutesService,
        private readonly navigationService: NavigationService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
    ) {
        this.createTabs();
        this.onResize();
    }

    ngAfterViewInit(): void {
        this.calendarContainerWidth = this.calendarContainer.nativeElement.clientWidth;
        this.calendarTitle.nativeElement.addEventListener('mousewheel', this.toggleZoom.bind(this))
    }

    ngOnInit(): void {

        this.canManageTMS = this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.TMS, FeatureAccessType.Manage);

        this.pixelsPerHour = 250;

        this.showAsTimeline = true;
        this.middleScreenTime = this.veriDate.createMoment(new Date());

        this.setTimeHeaders();

        if (this.showAsTimeline) {
            this.updateCurrentTimeMarker();
        }

        this.updateData(true, true);

        this.updateInterval = setInterval(() => {
            this.updateCurrentTimeMarker();

            this.updateData(false, true);
        }, 30_000);
    }

    ngOnDestroy(): void {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
    }

    onChildrenRendered(): void {
        this.viewport.checkViewportSize();
    }

    toggleZoom($event: MouseWheelEvent) {
        // Zoom hours on mouse wheel

        if ($event.deltaY < 0) {
            this.zoomOut();
        }
        if ($event.deltaY > 0) {
            this.zoomIn();
        }
        return false;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event?) {
        this.screenWidth = window.innerWidth;
    }

    goToScheduling(): void {
        this.navigationService.go("app.tms.scheduling");
    }

    zoomOut() {
        this.pixelsPerHour -= 50;
        this.setMinMaxZoom();

        this.updateCurrentTimeMarker();
        this.setPositionsOnStops(false);
    }

    zoomIn() {
        this.pixelsPerHour += 50;
        this.setMinMaxZoom();

        this.updateCurrentTimeMarker();
        this.setPositionsOnStops(false);
    }

    panLeftMouseDown($event: MouseEvent) {
        this.panLeftClickPressed = true;
        this.panLeftClickInitialX = $event.clientX;
        $event.preventDefault();
    }

    panLeftMouseMove($event: MouseEvent) {
        if (!this.panLeftClickPressed || this.calendarPanLimitsReached($event)) {

            return;
        }

        this.panLeftDistanceTravelledX = $event.clientX - this.panLeftClickInitialX;
        this.panLeftOffsetX = Math.round(this.panLeftOriginalOffsetX + this.panLeftDistanceTravelledX);

        this.updateRouteVisitVisibility();
        this.updateCalendarPanPosition();
    }

    panLeftMouseUp($event: MouseEvent) {
        if (this.panLeftClickPressed) {
            $event.preventDefault();
        }

        this.onMouseUp();
    }

    onMouseUp() {
        this.panLeftClickPressed = false;
        this.panLeftOriginalOffsetX = this.panLeftOffsetX;
    }

    calendarPanLimitsReached($event: MouseEvent): boolean {
        // Prevent scrolling past beginning/end of timeline
        if (this.calendarPanPosition > 0 && $event.clientX > this.panLeftClickInitialX) {
            return true;
        }

        const fullWidth = this.hoursToDisplay.length * this.pixelsPerHour * -1;
        const maxScrollRight = fullWidth + this.calendarContainerWidth;

        if (this.calendarPanPosition < maxScrollRight && $event.clientX < this.panLeftClickInitialX) {
            return true;
        }

        if (!this.showAsTimeline && $event.clientX < this.panLeftClickInitialX) {
            const longestRouteLength = Math.max(...this.displayRoutes.map(route => route.PlannedVisits.length));
            const pixelWidth = this.displayRoutes[0].displayPlannedVisits[0].pixelWidth;
            const maxScrollRightListView = longestRouteLength * pixelWidth + 70;

            if (Math.abs(this.calendarPanPosition) > maxScrollRightListView) {
                return true;
            }
        }

        return false;
    }

    syncScreen(offset = 0) {
        this.panLeftClickInitialX = 0;
        this.panLeftDistanceTravelledX = 0;
        this.panLeftOriginalOffsetX = offset;

        // The timeout gives us a nice animation
        setTimeout(() => {
            this.panLeftOffsetX = offset;

            this.updateCalendarPanPosition();
            this.updateRouteVisitVisibility();

        }, 50);
    }

    showRouteDetails(route: IRouteDetailDisplay) {

        // Prevent accidental clicks whilst panning
        if (this.panLeftOriginalOffsetX != this.panLeftOffsetX) {
            return;
        }

        this.plannedRouteService.openRouteDetailsDialog(route);
    }

    onFilterBarChange(event: IAggregateFilterEvent<IRouteDetailDisplay>) {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        // This is LIKELY because of the default (change) subscription?
        if (!event.trigger) {
            return;
        }

        this.displayRoutes = event.filteredData;
        this.setPositionsOnStops();
    }

    filterRoutesBySearchText = (r: IRouteDetailDisplay, text: string) => {
        return this.activeRoutesService.filterRouteByTextPredicate(r, text);
    }

    bringRouteIntoView(plannedRoute: IRouteDetailDisplay) {

        this.onMouseUp();
        let startTime = this.veriDate.createMoment(plannedRoute.PlannedStartTime);
        const plannedStartTimePixelPosition = this.getChartPixelPosition(startTime);

        this.syncScreen(this.currentTimePosition - plannedStartTimePixelPosition + 50);
    }

    toggleTimelineView() {
        this.showAsTimeline = !this.showAsTimeline;

        if (!this.showAsTimeline) {

            this.panLeftClickInitialX = 0;
            this.panLeftOffsetX = 0;
            this.calendarPanPosition = 0;
            this.panLeftDistanceTravelledX = 0;
            this.currentTimePosition = 0;
        }
        else {
            this.updateCurrentTimeMarker();
        }

        this.setPositionsOnStops();
    }

    scheduleARouteClicked(): void {
        this.navigationService.go("app.tms.scheduling.new");
    }

    trackByVisit(index: number, visit: IRouteDetailVisitDisplay): number {
        return visit.Id;
    }

    trackByPlannedRoute(index: number, route: IRouteDetailDisplay): number {
        return route.Id;
    }

    // ---

    private setTimeHeaders() {
        const now = this.veriDate.createMoment(new Date());

        now.startOf("d").add(-1, "d");
        for (let i = 0; i < 24 * 2.5; i++) {
            now.add(1, "h");

            this.hoursToDisplay.push({
                label: now.format("HH:mm"),
                time: now.clone()
            });
        }
    }

    private updateTimeHeaders() {
        // Add extra headers if current routes start/end outside of current header times
        if (!this.activeRoutes || this.activeRoutes.length == 0) {
            return;
        }

        const orderedRoutes = [...this.activeRoutes].sort((a, b) => {
            return new Date(a.PlannedStartTime) < new Date(b.PlannedStartTime) ? -1 : 1;
        });

        const earliestRouteStart = this.veriDate.createMoment(orderedRoutes[0].PlannedStartTime);
        const latestRouteEnd = this.veriDate.createMoment(orderedRoutes[orderedRoutes.length - 1].PlannedEndTime);

        if (this.veriDate.date1IsBeforeDate2(earliestRouteStart.toDate(), this.hoursToDisplay[0].time.toDate())) {
            const originalStart = this.hoursToDisplay[0].time;
            const hoursToAdd = Math.round(this.veriDate.getDiff(earliestRouteStart, originalStart) / 1000 / 60 / 60 + 3);

            for (let i = hoursToAdd; i >= 0; i--) {
                originalStart.add(-1, "h");
                this.hoursToDisplay.unshift({
                    label: originalStart.format("HH:mm"),
                    time: originalStart.clone()
                });
            }
        }
        else if (this.veriDate.date1IsBeforeDate2(this.hoursToDisplay[0].time.toDate(), latestRouteEnd.toDate())) {
            const originalEnd = this.hoursToDisplay[this.hoursToDisplay.length - 1].time;
            const hoursToAdd = Math.round(this.veriDate.getDiff(latestRouteEnd, originalEnd) / 1000 / 60 / 60 + 3);

            for (let i = hoursToAdd; i >= 0; i--) {
                originalEnd.add(1, "h");
                this.hoursToDisplay.push({
                    label: originalEnd.format("HH:mm"),
                    time: originalEnd.clone()
                });
            }
        }
    }

    private setMinMaxZoom() {
        this.pixelsPerHour = Math.min(this.screenWidth / 4, this.pixelsPerHour);
        this.pixelsPerHour = Math.max(150, this.pixelsPerHour);
    }

    private updateData(first = false, invalidateCache = false) {

        if (first) {
            this.loading = true;
        }

        const totalTimeWindowToGet = 12;

        var startTimeWindow = this.veriDate.createMoment(new Date()).add(-totalTimeWindowToGet / 2, "h");
        var endTimeWindow = this.veriDate.createMoment(new Date()).add(totalTimeWindowToGet / 2, "h");

        return this.plannedRouteService
            .getPlannedRoutes(startTimeWindow, endTimeWindow, { mapDrivers: true, mapVehicles: true, mapGeofences: true, invalidateCache: invalidateCache })
            .then(routes => {
                this.activeRoutes = routes as IRouteDetailDisplay[];
                this.activeRoutes.forEach(route => {
                    route.displayPlannedVisits = this.activeRoutesService.createDisplayPlannedVisitsOnRoute(route);
                    this.activeRoutesService.updateRouteVisits(route);
                });

                // Sort them by start time
                this.activeRoutes.sort((a, b) => this.veriDate.createMoment(a.PlannedStartTime).diff(b.PlannedStartTime));

                if (first) {
                    // Find out how wide they may be and adjust initial pixelsPerHour accordingly
                    let totalMins = 0;
                    let totalStopCount = 0;

                    this.activeRoutes.forEach(route => {
                        route.displayPlannedVisits.forEach(visit => {
                            let diff = this.veriDate.createMoment(visit.PlannedDepartureTime).diff(visit.PlannedArrivalTime, "m");
                            totalMins += diff;
                            totalStopCount++;
                        });
                    });

                    this.updateTimeHeaders();

                    if (totalStopCount > 0) {
                        const averageMinsPerStop = totalMins / totalStopCount;
                        const averageHoursPerStop = averageMinsPerStop / 60;

                        const idealPixelsPerStop = 100;
                        this.pixelsPerHour = idealPixelsPerStop / averageHoursPerStop;

                        this.setMinMaxZoom();

                        this.updateCurrentTimeMarker();
                    }
                }
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private getChartPixelPosition(time: moment.Moment) {
        const pixelsPerTimelineMinute = this.pixelsPerHour / 60;

        const firstTime = this.hoursToDisplay[0].time;
        const minsFromStartUntilNow = time.diff(firstTime, "m");

        return minsFromStartUntilNow * pixelsPerTimelineMinute;
    }

    private getChartTimeFromPixel(pixelPosition: number) {
        const pixelsPerTimelineMinute = this.pixelsPerHour / 60;

        const minsFromStartUntilNow = pixelPosition / pixelsPerTimelineMinute;
        const firstTime = this.hoursToDisplay[0].time;

        return firstTime.clone().add(-minsFromStartUntilNow, "m");
    }

    private updateCurrentTimeMarker() {
        if (!this.showAsTimeline) {
            return;
        }

        this.currentTimePosition = this.getChartPixelPosition(this.veriDate.createMoment(new Date())) - this.pixelsPerHour;
        this.updateCalendarPanPosition();
    }

    private setPositionsOnStops(updateVisibility = true) {

        if (!this.displayRoutes) {
            return;
        }

        const currentTime = this.veriDate.createMoment(new Date());

        this.displayRoutes.forEach((route, i) => {
            let previousStop = null;

            route.startTimeWindowPosition = this.getChartPixelPosition(this.veriDate.createMoment(route.PlannedStartTime).add(-1, "h"));
            route.endTimeWindowPosition = this.getChartPixelPosition(this.veriDate.createMoment(route.PlannedEndTime).add(1, "h"));

            route.displayPlannedVisits.forEach((visit, si) => {

                visit.startPixelPosition = Math.round(this.getChartPixelPosition(this.veriDate.createMoment(visit.PlannedArrivalTime)));
                visit.endPixelPosition = Math.round(this.getChartPixelPosition(this.veriDate.createMoment(visit.PlannedDepartureTime)));
                visit.pixelWidth = Math.round(visit.endPixelPosition - visit.startPixelPosition);

                visit.classSize = "";

                if (visit.pixelWidth < 120) {
                    visit.classSize = "pip";
                }
                else if (visit.pixelWidth < 160) {
                    visit.classSize = "medium";
                }

                visit.pixelsToNextStop = 0;

                if (previousStop) {
                    previousStop.pixelsToNextStop = visit.startPixelPosition - previousStop.endPixelPosition;

                    this.calculatePixelsTravelled(route, previousStop, visit, currentTime);
                }

                if (!this.showAsTimeline) {
                    // Overwrite these values for non calendar
                    const pixelMargin = 10;
                    visit.pixelWidth = 170;
                    visit.startPixelPosition = (si * (visit.pixelWidth + pixelMargin)) + pixelMargin;
                    visit.endPixelPosition = visit.startPixelPosition + visit.pixelWidth;
                    visit.classSize = "";
                }

                previousStop = visit;
            });
        });

        if (updateVisibility) {
            this.updateRouteVisitVisibility();
        }
    }

    private calculatePixelsTravelled(route: IRouteDetailDisplay, visit: IRouteDetailVisitDisplay, nextStop: IRouteDetailVisitDisplay, currentTime: moment.Moment) {

        if (visit.Status == RouteVisitStatus.Arrived || visit.Status == RouteVisitStatus.Departed) {
            // Ensure we are not missed, else we dont know if we went there or not?!
            if (nextStop.Status != RouteVisitStatus.Missed) {
                visit.actualPixelsTravelled = visit.pixelsToNextStop;

                // Add 5 so we dont see our nubbin
                visit.actualPixelsTravelled += 3;
            }
        }
        else if (route.currentVisit === visit && route.nextVisit && route.currentVisit !== route.nextVisit) {

            const currentTravelSecs = currentTime.diff(visit.PlannedDepartureTime, "s");

            const pixelsPerSecond = this.pixelsPerHour / 60 / 60;
            visit.actualPixelsTravelled = currentTravelSecs * pixelsPerSecond;
        }
        else {
            visit.actualPixelsTravelled = 0;
        }
    }

    private updateRouteVisitVisibility() {
        if (!this.displayRoutes) {
            return;
        }

        const screenBuffer = this.screenWidth - this.leftNavBuffer - 250; // 250 is the width of the user bar

        this.displayRoutes.forEach((route, i) => {
            route.hasVisibleVisits = false;

            route.displayPlannedVisits.forEach((visit, si) => {
                let insideLeftBounds = this.calendarPanPosition + visit.pixelsToNextStop > visit.endPixelPosition * -1;
                let insideRightBounds = this.calendarPanPosition - screenBuffer < visit.startPixelPosition * -1;
                visit.isVisible = insideLeftBounds && insideRightBounds;

                route.hasVisibleVisits = route.hasVisibleVisits || visit.isVisible;

                visit.isLeftOfScreen = !insideLeftBounds && insideRightBounds;
            });
        });
    }

    private updateCalendarPanPosition() {
        if (this.showAsTimeline) {
            this.calendarPanPosition = Math.round((this.currentTimePosition * -1) + this.panLeftOffsetX);
            const middleScreenPosition = this.calendarPanPosition + this.leftNavBuffer - (this.screenWidth / 2);
            this.middleScreenTime = this.getChartTimeFromPixel(middleScreenPosition);
        }
        else {
            this.calendarPanPosition = this.panLeftOffsetX;
        }

        const halfScreenWidth = this.screenWidth / 2;

        this.showSyncButtonLeft = false;
        this.showSyncButtonRight = false;

        if (Math.abs(this.calendarPanPosition) - this.currentTimePosition > halfScreenWidth - this.leftNavBuffer) {
            this.showSyncButtonLeft = true;
        }
        else if (Math.abs(this.currentTimePosition) - Math.abs(this.calendarPanPosition) > this.screenWidth / 2) {
            this.showSyncButtonRight = true;
        }
    }

    private createTabs(): void {
        this.filterTabs = [];
        
        this.filterTabs.push({ title: this.translationService.t("Common.All"), predicate: () => true });
        this.filterTabs.push({ title: this.translationService.t("Tms.InProgress"), predicate: x => x.Status == RouteStatus.Started });
        this.filterTabs.push({ title: this.translationService.t("Tms.Completed"), predicate: x => x.Status == RouteStatus.Completed || x.Status == RouteStatus.Incomplete || x.Status == RouteStatus.CompletedWithMissing });
        this.filterTabs.push({ title: this.translationService.t("Tms.Incomplete"), predicate: x => x.Status == RouteStatus.Incomplete || x.Status == RouteStatus.CompletedWithMissing });
        this.filterTabs.push({ title: this.translationService.t("Tms.Planned"), predicate: x => x.Status == RouteStatus.Planned });
        this.filterTabs.push({ title: this.translationService.t("Tms.Expired"), predicate: x => x.Status == RouteStatus.Expired || x.Status == RouteStatus.UserExpired });
    }
}
